/****************
 * LESSONS TIED TO WHAT JUST HAPPENED. 2026-10-21, engagement second pass.
 *
 * Guillaume, 2026-10-21: short lessons tied to the moment ("you just had your
 * first losing trade: here's how drawdowns work"), and loss context ("normal
 * for this trader") is part of it. Each lesson is unlocked by a fact about the
 * person's own copies; the text lives in common.json (lesson.<id>.*), the same
 * on web and phone.
 *
 * Twin file: frontend/src/lib/lessons.core.ts and
 * mobile-app/src/lib/lessons.core.ts must be byte-identical.
 *
 * Lessons explain. None suggests a setting, and none says to copy more after a
 * loss (copyfi-no-defaults-from-us; Orb rulings 10-15).
 ****************/
import type { MilestoneState, Recap, RecapSide } from './engagement.core';

export type LessonId =
  | 'howCopyWorks'
  | 'lossesArePart'
  | 'drawdowns'
  | 'oneWin'
  | 'readRecord'
  | 'killSwitch'
  | 'beforeLive'
  | 'spreadRisk';

/** Every lesson, in the order the Learn page lists them. */
export const LESSONS: LessonId[] = [
  'howCopyWorks', 'oneWin', 'lossesArePart', 'drawdowns', 'readRecord', 'spreadRisk', 'killSwitch', 'beforeLive',
];

/** Paragraph count of each lesson's body (lesson.<id>.p1..pN). */
export const LESSON_PARAGRAPHS: Record<LessonId, number> = {
  howCopyWorks: 3, oneWin: 3, lossesArePart: 3, drawdowns: 3, readRecord: 3, spreadRisk: 3, killSwitch: 3, beforeLive: 3,
};

const WEEK = 7 * 86_400_000;

function reachedRecently(m: MilestoneState | undefined, key: string, now: number): boolean {
  const x = m?.milestones.find((v) => v.key === key);
  return !!x?.done && !!x.achieved_at && now - new Date(x.achieved_at).getTime() < WEEK;
}

function lossSide(r: Recap | undefined): RecapSide | null {
  if (!r) return null;
  if (r.live.worst) return r.live;
  if (r.paper.worst) return r.paper;
  return null;
}

/**
 * The lessons unlocked right now, most relevant first. `killSwitchedRecently`
 * comes from the screen's own session list (a session its kill switch stopped
 * in the last week).
 */
export function lessonsForNow(
  m: MilestoneState | undefined,
  r: Recap | undefined,
  killSwitchedRecently: boolean,
  now: number = Date.now(),
): LessonId[] {
  const out: LessonId[] = [];
  if (killSwitchedRecently) out.push('killSwitch');
  const loss = lossSide(r);
  if (loss) out.push('lossesArePart');
  if (loss && loss.losses >= 2) out.push('drawdowns');
  if (reachedRecently(m, 'first_trade', now)) out.push('howCopyWorks');
  if (reachedRecently(m, 'first_win', now)) out.push('oneWin');
  if (reachedRecently(m, 'ten_trades', now)) out.push('readRecord');
  const by = new Map((m?.milestones ?? []).map((x) => [x.key, x]));
  if (by.get('first_trade')?.done && !by.get('three_traders')?.done) out.push('spreadRisk');
  if (by.get('ten_trades')?.done && !by.get('first_live')?.done) out.push('beforeLive');
  return out;
}

/** Lessons tied to something that just happened to the person's own copies:
 *  the only ones the Dashboard shows (the rest live in Learn). 2026-10-21. */
export const CONTEXT_LESSONS: LessonId[] = ['killSwitch', 'lossesArePart', 'drawdowns'];

/** The first unlocked, contextual lesson not yet read on this device, for the Dashboard card. */
export function lessonToShow(unlocked: LessonId[], seen: Set<string>): LessonId | null {
  return unlocked.find((l) => CONTEXT_LESSONS.includes(l) && !seen.has(l)) ?? null;
}

/* ── Loss context: "within this trader's usual range" ──────────────────── */

export interface LossContext {
  /** This trade's loss, percent on margin, positive. */
  lossPct: number;
  /** The trader's worst trade on record, percent on margin, positive. */
  traderWorstPct: number;
  winRate: number;
  trades: number;
  /** within: no worse than the trader's worst on record; beyond: worse. */
  verdict: 'within' | 'beyond';
}

/** The recap's hardest trade read against the copied trader's record, or null. */
export function lossContext(r: Recap | undefined): LossContext | null {
  const side = lossSide(r);
  const w = side?.worst;
  const c = side?.worst_context;
  if (!w || !c || !(c.trade_count > 0)) return null;
  const lossPct = Math.abs(Math.min(0, w.roe));
  return {
    lossPct,
    traderWorstPct: c.worst_trade_percent,
    winRate: c.win_rate,
    trades: c.trade_count,
    verdict: lossPct <= c.worst_trade_percent ? 'within' : 'beyond',
  };
}
